import config from "../config";

const appsScriptUrl = config.appscript_url;

interface Funnel {
    funnelId: string;
    title: string;
    slug: string;
    generatedHtml: string;
    createdDate: string;
    updatedDate: string;
}

interface FunnelFormData {
    title: string;
    slug: string;
    ctaLink: string;
    prompt: string;
}

interface FunnelsResponse {
    success: boolean;
    funnels?: Funnel[];
    stats?: {
        total: number;
        page: number;
        pages: number;
    };
    error?: string;
}

interface FunnelResponse {
    success: boolean;
    funnel?: Funnel;
    funnelId?: string;
    slug?: string;
    message?: string;
    error?: string;
}

const funnelApi = {
    // Save funnel to Google Sheet
    saveFunnel: async (funnelData: {
        funnelId?: string;
        title: string;
        slug: string;
        generatedHtml: string;
    }): Promise<FunnelResponse> => {
        try {
            const response = await fetch(appsScriptUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "text/plain",
                },
                body: JSON.stringify({
                    action: "saveFunnel",
                    ...funnelData,
                }),
            });

            const text = await response.text();
            return JSON.parse(text);
        } catch (error) {
            console.error("Error saving funnel:", error);
            return { success: false, error: "Failed to save funnel" };
        }
    },

    // Get funnel by ID
    getFunnel: async (funnelId: string): Promise<FunnelResponse> => {
        try {
            const params = new URLSearchParams({
                action: "getFunnel",
                id: funnelId,
            });

            const response = await fetch(`${appsScriptUrl}?${params}`, {
                method: "GET",
            });

            const text = await response.text();
            return JSON.parse(text);
        } catch (error) {
            console.error("Error getting funnel:", error);
            return { success: false, error: "Failed to get funnel" };
        }
    },

    // Get funnel by slug
    getFunnelBySlug: async (slug: string): Promise<FunnelResponse> => {
        try {
            const params = new URLSearchParams({
                action: "getFunnelBySlug",
                slug: slug,
            });

            const response = await fetch(`${appsScriptUrl}?${params}`, {
                method: "GET",
            });

            const text = await response.text();
            return JSON.parse(text);
        } catch (error) {
            console.error("Error getting funnel by slug:", error);
            return { success: false, error: "Failed to get funnel" };
        }
    },

    // Get all funnels
    getFunnels: async (
        page: number = 1,
        limit: number = 20
    ): Promise<FunnelsResponse> => {
        try {
            const params = new URLSearchParams({
                action: "getFunnels",
                page: page.toString(),
                limit: limit.toString(),
            });

            const response = await fetch(`${appsScriptUrl}?${params}`, {
                method: "GET",
            });

            const text = await response.text();
            return JSON.parse(text);
        } catch (error) {
            console.error("Error getting funnels:", error);
            return { success: false, error: "Failed to get funnels" };
        }
    },

    // Delete funnel
    deleteFunnel: async (funnelId: string): Promise<FunnelResponse> => {
        try {
            const response = await fetch(appsScriptUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "text/plain",
                    "Authorization": `Bearer ${config.api_key}`,
                },
                body: JSON.stringify({
                    action: "deleteFunnel",
                    funnelId,
                }),
            });

            const text = await response.text();
            const data = JSON.parse(text);
            return data;
        } catch (error) {
            console.error("Error deleting funnel:", error);
            return { success: false, error: "Failed to delete funnel" };
        }
    },

    // Generate funnel with Groq AI
    generateWithAI: async (data: FunnelFormData): Promise<{ success: boolean; html?: string; error?: string }> => {
        try {
            const groqKey = config.groq_key;

            if (!groqKey || groqKey === '' || groqKey === 'gsk_YOUR_KEY_HERE') {
                return { 
                    success: false, 
                    error: "GROQ API key not configured. Please add REACT_APP_GROQ_KEY to your .env file." 
                };
            }

            const prompt = `${data.prompt}

IMPORTANT: All Buy Now / CTA buttons must link to: ${data.ctaLink}

OUTPUT REQUIREMENTS:
- Return ONLY raw HTML code starting with <!DOCTYPE html>
- No markdown code blocks (no \`\`\`)
- No explanations, no comments
- Use any styling you prefer: Tailwind CSS, inline styles, or <style> tags

Generate the HTML now.`;

            const response = await fetch('https://api.groq.com/openai/v1/chat/completions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${groqKey}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    model: 'llama-3.1-8b-instant',
                    messages: [
                        {
                            role: 'user',
                            content: prompt
                        }
                    ],
                    temperature: 0.8,
                    max_tokens: 4000,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Groq API error:', errorData);
                return { 
                    success: false, 
                    error: errorData.error?.message || 'Failed to generate funnel' 
                };
            }

            const result = await response.json();
            let generatedHtml = result.choices?.[0]?.message?.content || '';

            if (!generatedHtml) {
                return { success: false, error: 'No HTML generated' };
            }

            // Clean up markdown code blocks if present
            generatedHtml = generatedHtml
                .replace(/^```html\s*/i, '')
                .replace(/^```\s*/i, '')
                .replace(/```$/i, '')
                .trim();

            return { success: true, html: generatedHtml };
        } catch (error) {
            console.error('Error generating funnel:', error);
            return { success: false, error: 'Failed to generate funnel: ' + error };
        }
    },
};

export default funnelApi;
export type { Funnel, FunnelFormData, FunnelResponse, FunnelsResponse };
